

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '../styles/theme.css';
import '../styles/grid-modern.css';
import type {
  DataGridProps,
  GridRow,
  GridColumn,
  SelectionContext} from '../types/gridTypes';
import { validateCell } from '../utils/validation';
import { getSelectionRange } from '../utils/selection';
import { IconAttachment, IconComment, IconChevronRight, IconChevronDown, IconSummary, IconMoreVertical, IconSortAsc, IconSortDesc, IconSettings, IconEyeOff, IconLock, IconInfo } from './icons';
// Story 6.4: FormulaCell for formula column rendering with error display
import { FormulaCell } from './FormulaCell';
import { isFormulaErrorCode } from './FormulaErrorTooltip';

// Extend props to include range capture support and formula edit start (Story 6.5)
interface ExtendedDataGridProps extends DataGridProps {
  isFormulaRangeCaptureActive?: boolean;
  onRangeCaptured?: (expr: string) => void;
  /** Story 6.5: Callback when user types `=` in a cell to start formula editing */
  onFormulaEditStart?: (rowId: string, colId: string, initialValue: string) => void;
  /** Story 6.5: Callback when cell value changes during editing (for formula bar sync) */
  onCellEditChange?: (value: string) => void;
  /** Story 6.5: External value to sync with cell editor (from formula bar) */
  externalEditValue?: string;
}

export const DataGrid: React.FC<ExtendedDataGridProps> = ({
  gridId,
  columns,
  rows,
  selection, // Controlled selection
  onChange,
  onSelectionChange,
  onAttachFiles,
  onOpenAttachments,
  onOpenComments,
  onUndo,
  onRedo,
  onSort,
  sortConfig,
  readOnly,
  validationErrors,
  emptyState,
  onEditColumn,
  onInsertColumn,
  onDeleteColumn,
  onToggleColumnVisibility,
  onColumnReorder,
  onRowConfig,
  onRowReorder,
  isFormulaRangeCaptureActive,
  onRangeCaptured,
  onFormulaEditStart, // Story 6.5: Formula bar trigger
  onCellEditChange, // Story 6.5: Formula bar sync
  externalEditValue, // Story 6.5: Formula bar sync
}) => {
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  // Freeze functionality disabled for now - will be added later
  const frozenCount = 0; 

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ r: string, c: string } | null>(null);
  const [dragOverRowId, setDragOverRowId] = useState<string | null>(null);
  
  // Row Reorder State
  const [reorderDragRowId, setReorderDragRowId] = useState<string | null>(null);
  const [reorderTargetId, setReorderTargetId] = useState<string | null>(null);

  // Column Reorder State
  const [reorderDragColId, setReorderDragColId] = useState<string | null>(null);
  const [reorderTargetColId, setReorderTargetColId] = useState<string | null>(null);

  const [anchorRowId, setAnchorRowId] = useState<string | null>(null);
  const [anchorColId, setAnchorColId] = useState<string | null>(null);

  const [resizingCol, setResizingCol] = useState<string | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number, w: number } | null>(null);
  const [editingCell, setEditingCell] = useState<{ r: string, c: string } | null>(null);
  
  const pendingCharRef = useRef<string | null>(null);
  const [clipboardBounds, setClipboardBounds] = useState<{ minR: number, maxR: number, minC: number, maxC: number } | null>(null);

  const [contextMenu, setContextMenu] = useState<{ x: number, y: number, type: 'row' | 'cell' | 'header', targetId: string, targetColId?: string } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Filter visible columns
  const visibleColumns = useMemo(() => columns.filter(c => !c.hidden), [columns]);

  // Auto-scroll to primary cell when selection changes
  useEffect(() => {
    if (selection.primaryRowId && selection.primaryColumnId) {
      const cellId = `cell-${selection.primaryRowId}-${selection.primaryColumnId}`;
      const el = document.getElementById(cellId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        // Add ephemeral flash class
        el.classList.add('flash-highlight');
        const timer = setTimeout(() => el.classList.remove('flash-highlight'), 1500);
        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, [selection.primaryRowId, selection.primaryColumnId]);

  const { visibleRows, depthMap, hasChildrenMap } = useMemo(() => {
    const visible: GridRow[] = [];
    const collapsedIds = new Set<string>();
    const depths = new Map<string, number>();
    const rowMap = new Map<string, GridRow>(rows.map(r => [r.id, r] as [string, GridRow]));
    const childrenMap = new Map<string, boolean>();
    rows.forEach(r => { if (r.parentId) childrenMap.set(r.parentId, true); });

    const getDepth = (rId: string): number => {
        if (depths.has(rId)) return depths.get(rId)!;
        const r = rowMap.get(rId);
        if (!r || !r.parentId) { depths.set(rId, 0); return 0; }
        const d = getDepth(r.parentId) + 1;
        depths.set(rId, d);
        return d;
    };

    for (const row of rows) {
        getDepth(row.id);
        const parentIsCollapsed = row.parentId && collapsedIds.has(row.parentId);
        if (parentIsCollapsed) { collapsedIds.add(row.id); continue; }
        visible.push(row);
        if (row.expanded === false) collapsedIds.add(row.id);
    }
    return { visibleRows: visible, depthMap: depths, hasChildrenMap: childrenMap };
  }, [rows]);

  const getIndices = (rId: string, cId: string) => {
    const rIdx = visibleRows.findIndex(r => r.id === rId);
    const cIdx = visibleColumns.findIndex(c => c.id === cId);
    return { rIdx, cIdx };
  };

  const selectCell = (rId: string, cId: string, mode: 'cell' | 'range' = 'cell', isDrag = false) => {
    const newSel: SelectionContext = {
      gridId,
      mode,
      primaryRowId: rId,
      primaryColumnId: cId,
      selectedRowIds: [rId],
      selectedColumnIds: [cId]
    };
    onSelectionChange?.(newSel);
    if (!isDrag) { 
        setDragStart({ r: rId, c: cId }); 
        setAnchorRowId(rId); 
        setAnchorColId(cId); 
    }
  };

  const handleCellClick = (rId: string, cId: string, e: React.MouseEvent) => {
    if (e.button === 2) return;
    containerRef.current?.focus();
    setContextMenu(null);

    // For checkbox columns, single click toggles the value
    const col = visibleColumns.find(c => c.id === cId);
    if (col?.type === 'checkbox' && !readOnly) {
      const row = rows.find(r => r.id === rId);
      if (row) {
        const currentVal = row.values[cId];
        const isChecked = currentVal === true || currentVal === 'true' || currentVal === 1;
        const newRows = rows.map(r => {
          if (r.id !== rId) return r;
          return { ...r, values: { ...r.values, [cId]: !isChecked } };
        });
        onChange?.(newRows);
        selectCell(rId, cId, 'cell');
        return;
      }
    }

    // If we are in formula range capture mode, clicking acts as a start of drag or single select capture
    // But actual capture logic is usually on MouseUp to support ranges.

    if (e.shiftKey && selection.primaryRowId && selection.primaryColumnId) {
      const aRId = anchorRowId || selection.primaryRowId;
      const aCId = anchorColId || selection.primaryColumnId;
      const range = getSelectionRange(visibleRows, visibleColumns, aRId, aCId, rId, cId);

      const newSel: SelectionContext = {
          ...selection,
          mode: 'range',
          primaryRowId: rId,
          primaryColumnId: cId,
          selectedRowIds: range.rowIds,
          selectedColumnIds: range.colIds
      };
      onSelectionChange?.(newSel);
      if (!anchorRowId) { setAnchorRowId(aRId); setAnchorColId(aCId); }
    } else {
      selectCell(rId, cId, 'cell');
    }
  };

  const handleCellMouseDown = (rId: string, cId: string, e: React.MouseEvent) => {
    if (e.button === 2) return;
    if (e.shiftKey) return;
    e.preventDefault(); // Prevent text selection
    containerRef.current?.focus();
    setContextMenu(null);
    setIsDragging(true);
    selectCell(rId, cId, 'cell', false);
  };

  const handleCellMouseEnter = (rId: string, cId: string) => {
    if (isDragging && dragStart) {
      const range = getSelectionRange(visibleRows, visibleColumns, dragStart.r, dragStart.c, rId, cId);
      const newSel: SelectionContext = { ...selection, mode: 'range', selectedRowIds: range.rowIds, selectedColumnIds: range.colIds };
      onSelectionChange?.(newSel);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
        setIsDragging(false);

        // Formula Range Capture Logic
        if (isFormulaRangeCaptureActive && onRangeCaptured && selection.selectedRowIds.length > 0 && selection.selectedColumnIds.length > 0) {
            // Calculate range indices
            const rIndices = selection.selectedRowIds.map(id => visibleRows.findIndex(r => r.id === id)).filter(i => i > -1);
            const cIndices = selection.selectedColumnIds.map(id => visibleColumns.findIndex(c => c.id === id)).filter(i => i > -1);
            
            if (rIndices.length > 0 && cIndices.length > 0) {
                const startRow = Math.min(...rIndices) + 1; // 1-based
                const endRow = Math.max(...rIndices) + 1;   // 1-based
                const colId = selection.selectedColumnIds[0]; // Take first column
                const col = visibleColumns.find(c => c.id === colId);
                
                if (col) {
                   let rangeExpr: string;
                   if (selection.selectedColumnIds.length === 1 && selection.selectedRowIds.length === visibleRows.length) {
                     // Whole column selected
                     rangeExpr = `[${col.label}]`;
                   } else {
                     // Range
                     const startColLetter = indexToColumnLetter(Math.min(...cIndices));
                     const endColLetter = indexToColumnLetter(Math.max(...cIndices));
                     rangeExpr = `${startColLetter}${startRow}:${endColLetter}${endRow}`;
                   }
                   onRangeCaptured(rangeExpr);
                }
            }
        }
        
        // Re-emit to confirm final state if needed
        if (selection.mode !== 'none') onSelectionChange?.(selection);
    }
  };

  // ... (Rest of the file unchanged) ...

  const handleRowHeaderClick = (rId: string, e: React.MouseEvent) => {
    if (e.button === 2) return; 
    containerRef.current?.focus();
    setContextMenu(null);
    if (e.ctrlKey || e.metaKey) {
        let newRowIds = [...selection.selectedRowIds];
        if (newRowIds.includes(rId)) newRowIds = newRowIds.filter(id => id !== rId); else newRowIds.push(rId);
        const primary = newRowIds.includes(selection.primaryRowId || '') ? selection.primaryRowId : (newRowIds[newRowIds.length - 1] || null);
        const newSel: SelectionContext = { gridId, mode: 'row', primaryRowId: primary || undefined, selectedRowIds: newRowIds, selectedColumnIds: visibleColumns.map(c => c.id) };
        setAnchorRowId(rId); onSelectionChange?.(newSel);
        return;
    }
    if (e.shiftKey && anchorRowId) {
        const startIdx = visibleRows.findIndex(r => r.id === anchorRowId);
        const endIdx = visibleRows.findIndex(r => r.id === rId);
        if (startIdx > -1 && endIdx > -1) {
             const min = Math.min(startIdx, endIdx);
             const max = Math.max(startIdx, endIdx);
             const rangeIds = visibleRows.slice(min, max + 1).map(r => r.id);
             const newSel: SelectionContext = { gridId, mode: 'row', primaryRowId: anchorRowId, selectedRowIds: rangeIds, selectedColumnIds: visibleColumns.map(c => c.id) };
             onSelectionChange?.(newSel);
        }
        return;
    }
    setAnchorRowId(rId);
    const newSel: SelectionContext = { gridId, mode: 'row', primaryRowId: rId, selectedRowIds: [rId], selectedColumnIds: visibleColumns.map(c => c.id) };
    onSelectionChange?.(newSel);
  };

  // ... (Header click, context menus, etc. preserved) ...

  const handleColumnHeaderClick = (cId: string, e: React.MouseEvent) => {
    if (e.button === 2) return;
    containerRef.current?.focus();
    setContextMenu(null);
    if (e.ctrlKey || e.metaKey) {
        let newColIds = [...selection.selectedColumnIds];
        if (newColIds.includes(cId)) newColIds = newColIds.filter(id => id !== cId); else newColIds.push(cId);
        const primary = newColIds.includes(selection.primaryColumnId || '') ? selection.primaryColumnId : (newColIds[newColIds.length - 1] || null);
        const newSel: SelectionContext = { gridId, mode: 'column', primaryColumnId: primary || undefined, selectedColumnIds: newColIds, selectedRowIds: visibleRows.map(r => r.id) };
        setAnchorColId(cId); onSelectionChange?.(newSel);
        return;
    }
    if (e.shiftKey && anchorColId) {
        const startIdx = visibleColumns.findIndex(c => c.id === anchorColId);
        const endIdx = visibleColumns.findIndex(c => c.id === cId);
        if (startIdx > -1 && endIdx > -1) {
            const min = Math.min(startIdx, endIdx);
            const max = Math.max(startIdx, endIdx);
            const rangeIds = visibleColumns.slice(min, max + 1).map(c => c.id);
            const newSel: SelectionContext = { gridId, mode: 'column', primaryColumnId: anchorColId, selectedColumnIds: rangeIds, selectedRowIds: visibleRows.map(r => r.id) };
            onSelectionChange?.(newSel);
        }
        return;
    }
    setAnchorColId(cId);
    const newSel: SelectionContext = { gridId, mode: 'column', primaryColumnId: cId, primaryRowId: visibleRows[0]?.id, selectedColumnIds: [cId], selectedRowIds: visibleRows.map(r => r.id) };
    onSelectionChange?.(newSel);
  };

  const handleContextMenu = (e: React.MouseEvent, rId: string, cId?: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, type: cId ? 'cell' : 'row', targetId: rId, targetColId: cId });
    const isRowInSelection = selection.selectedRowIds.includes(rId);
    const isCellInSelection = cId && selection.selectedColumnIds.includes(cId);
    if (!isRowInSelection && !cId) {
         setAnchorRowId(rId);
         const newSel: SelectionContext = { gridId, mode: 'row', primaryRowId: rId, selectedRowIds: [rId], selectedColumnIds: visibleColumns.map(c => c.id) };
         onSelectionChange?.(newSel);
    } else if (cId && (!isRowInSelection || !isCellInSelection)) {
         selectCell(rId, cId, 'cell');
    }
  };

  const handleHeaderContextMenu = (e: React.MouseEvent, colId: string) => {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY, type: 'header', targetId: colId });
  };

  const handleMenuButtonClick = (e: React.MouseEvent, colId: string) => {
      e.stopPropagation();
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      setContextMenu({ x: rect.left, y: rect.bottom, type: 'header', targetId: colId });
  };

  const handleEditColumn = () => {
      if (contextMenu?.type !== 'header') return;
      onEditColumn?.(contextMenu.targetId);
      setContextMenu(null);
  };

  const handleHideColumn = () => {
      if (contextMenu?.type !== 'header') return;
      onToggleColumnVisibility?.(contextMenu.targetId);
      setContextMenu(null);
  };

  const handleInspectColumnTypes = () => {
      if (contextMenu?.type !== 'header') return;
      const colId = contextMenu.targetId;
      const col = visibleColumns.find(c => c.id === colId);
      if (!col) return;

      // Analyze types in the column
      const typeCounts: Record<string, number> = {};
      const examples: Record<string, any[]> = {};
      rows.forEach(row => {
          const val = row.values[colId];
          const type = val === null || val === undefined ? 'null/undefined' :
                       typeof val === 'string' ? 'string' :
                       typeof val === 'number' ? 'number' :
                       typeof val === 'boolean' ? 'boolean' :
                       'object';
          typeCounts[type] = (typeCounts[type] || 0) + 1;
          if (!examples[type]) examples[type] = [];
          if (examples[type].length < 3) examples[type].push(val);
      });

      const summary = Object.entries(typeCounts)
          .map(([type, count]) => `${type}: ${count} (${examples[type]?.join(', ') || ''})`)
          .join('\n');

      alert(`Column "${col.label}" types:\n${summary}`);
      setContextMenu(null);
  };

  // Freeze functionality disabled for now - will be added later
  // const handleFreezeColumn = () => { ... };
  // const handleUnfreeze = () => { ... };

  const handleSortTrigger = () => {
      if (contextMenu?.type !== 'header') return;
      onSort?.(contextMenu.targetId);
      setContextMenu(null);
  };

  // --- Row Drag and Drop ---
  const handleRowDragStart = (e: React.DragEvent, rowId: string) => {
    e.stopPropagation();
    setReorderDragRowId(rowId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', rowId);
  };

  const handleRowDragOver = (e: React.DragEvent, rowId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (reorderDragRowId && reorderDragRowId !== rowId) {
       setReorderTargetId(rowId);
    }
  };

  const handleRowDragLeave = () => {
    setReorderTargetId(null);
  };

  const handleRowDrop = (e: React.DragEvent, rowId: string) => {
     e.preventDefault();
     e.stopPropagation();
     if (reorderDragRowId && reorderDragRowId !== rowId) {
         onRowReorder?.(reorderDragRowId, rowId);
     }
     setReorderDragRowId(null);
     setReorderTargetId(null);
  };

  // --- Column Drag and Drop ---
  const handleColDragStart = (e: React.DragEvent, colId: string) => {
    e.stopPropagation();
    setReorderDragColId(colId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('colId', colId);
  };

  const handleColDragOver = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    if (reorderDragColId && reorderDragColId !== colId) {
        setReorderTargetColId(colId);
    }
  };

  const handleColDragLeave = () => {
      setReorderTargetColId(null);
  };

  const handleColDrop = (e: React.DragEvent, colId: string) => {
      e.preventDefault();
      if (reorderDragColId && reorderDragColId !== colId) {
          onColumnReorder?.(reorderDragColId, colId);
      }
      setReorderDragColId(null);
      setReorderTargetColId(null);
  };


  const handleDoubleClick = (rId: string, cId: string) => {
    if (readOnly) return;
    const row = rows.find(r => r.id === rId);
    if (row && row.aggregationConfig?.enabled && row.aggregationConfig.perColumn?.[cId] && row.aggregationConfig.perColumn[cId] !== 'none') {
        return;
    }

    // For checkbox columns, toggle value on double-click instead of editing
    const col = visibleColumns.find(c => c.id === cId);
    if (col?.type === 'checkbox' && row) {
      const currentVal = row.values[cId];
      const isChecked = currentVal === true || currentVal === 'true' || currentVal === 1;
      const newRows = rows.map(r => {
        if (r.id !== rId) return r;
        return { ...r, values: { ...r.values, [cId]: !isChecked } };
      });
      onChange?.(newRows);
      return;
    }

    pendingCharRef.current = null;
    setEditingCell({ r: rId, c: cId });
  };

  const commitEdit = (valueOverride?: any) => {
    if (!editingCell) return;
    const { r, c } = editingCell;
    
    let val: any;
    
    if (valueOverride !== undefined) {
      val = valueOverride;
    } else if (editInputRef.current) {
      val = editInputRef.current.value;
    } else {
      return;
    }

    const col = visibleColumns.find(cl => cl.id === c);
    if (col?.type === 'multi-select') {
        if (Array.isArray(val)) {
            // Already array
        } else if (typeof val === 'string') {
            val = val.split(',').map(s => s.trim()).filter(s => s !== '');
        } else {
            val = [];
        }
    }

    const newRows = rows.map(row => {
      if (row.id !== r) return row;
      const updatedRow = { ...row, values: { ...row.values } };
      updatedRow.values[c] = val;
      // Clear formula if value is empty
      if (!val || val === '') {
        updatedRow.formulas = { ...updatedRow.formulas };
        delete updatedRow.formulas[c];
      }
      return updatedRow;
    });
    onChange?.(newRows);
    setEditingCell(null);
    pendingCharRef.current = null;
    containerRef.current?.focus();
    onCellEditChange?.('');
  };

  const generateId = () => 'row_' + Math.random().toString(36).substr(2, 9);
  const handleAddRow = () => { const newRow: GridRow = { id: generateId(), values: {}, expanded: true }; const newRows = [...rows, newRow]; onChange?.(newRows); return newRow.id; };
  
  const handleInsertRow = (direction: 'above' | 'below') => {
    if (contextMenu?.type !== 'row' && contextMenu?.type !== 'cell') return;
    const targetIdx = rows.findIndex(r => r.id === contextMenu.targetId);
    if (targetIdx === -1) return;
    const targetRow = rows[targetIdx];
    const newRow: GridRow = { id: generateId(), parentId: targetRow.parentId, values: {}, expanded: true };
    const newRows = [...rows];
    newRows.splice(direction === 'below' ? targetIdx + 1 : targetIdx, 0, newRow);
    onChange?.(newRows); setContextMenu(null);
  };

  const handleDuplicateRow = () => {
    const selectedIds = selection.selectedRowIds;
    if (selectedIds.length === 0) return;
    const toDuplicate = new Set(selectedIds);
    const nextRows: GridRow[] = [];
    rows.forEach(r => {
        nextRows.push(r);
        if (toDuplicate.has(r.id)) {
            nextRows.push({ ...r, id: generateId(), values: { ...r.values }, formulas: r.formulas ? { ...r.formulas } : undefined, attachments: [], comments: [], attachmentCount: 0, commentCount: 0 });
        }
    });
    onChange?.(nextRows); setContextMenu(null);
  };

  const getDescendantIds = (rowIds: string[], allRows: GridRow[]): Set<string> => {
    const descendants = new Set<string>();
    const childrenMap = new Map<string, string[]>();
    allRows.forEach(r => { if (r.parentId) { if (!childrenMap.has(r.parentId)) childrenMap.set(r.parentId, []); childrenMap.get(r.parentId)?.push(r.id); } });
    const queue = [...rowIds];
    while (queue.length > 0) {
      const currentId = queue.pop()!;
      const children = childrenMap.get(currentId);
      if (children) children.forEach(childId => { if (!descendants.has(childId)) { descendants.add(childId); queue.push(childId); } });
    }
    return descendants;
  };

  const handleDeleteRows = () => {
    const selectedIds = selection.selectedRowIds;
    if (selectedIds.length === 0 && contextMenu?.targetId) {
        selectedIds.push(contextMenu.targetId);
    }
    if (selectedIds.length === 0) return;
    const descendants = getDescendantIds(selectedIds, rows);
    const toDelete = new Set([...selectedIds, ...descendants]);
    const newRows = rows.filter(r => !toDelete.has(r.id));
    onChange?.(newRows); setContextMenu(null); 
    onSelectionChange?.({ gridId, mode: 'none', selectedRowIds: [], selectedColumnIds: [] });
  };

  const handleCopy = () => {
    if (selection.selectedRowIds.length === 0) return;
    const rIndices = selection.selectedRowIds.map(id => rows.findIndex(r => r.id === id)).filter(i => i > -1).sort((a, b) => a - b);
    const cIndices = selection.selectedColumnIds.map(id => visibleColumns.findIndex(c => c.id === id)).filter(i => i > -1).sort((a, b) => a - b);
    if (rIndices.length === 0 || cIndices.length === 0) return;
    const orderedRows = rIndices.map(i => rows[i]);
    const orderedCols = cIndices.map(i => visibleColumns[i]);
    const text = orderedRows.map(r => orderedCols.map(c => { const val = r.values[c.id]; return val == null ? '' : String(val); }).join('\t')).join('\n');
    navigator.clipboard.writeText(text);
    setClipboardBounds({ minR: rIndices[0], maxR: rIndices[rIndices.length - 1], minC: cIndices[0], maxC: cIndices[cIndices.length - 1] });
    setContextMenu(null);
  };

  const handleNativePaste = (e: React.ClipboardEvent) => {
      if (editingCell) return;
      e.preventDefault();
      const text = e.clipboardData.getData('text');
      if (!text) return;
      const startRId = selection.primaryRowId || selection.selectedRowIds[0];
      const startCId = selection.primaryColumnId || selection.selectedColumnIds[0];
      if (!startRId || !startCId) return;
      const startRIdx = rows.findIndex(r => r.id === startRId);
      const startCIdx = visibleColumns.findIndex(c => c.id === startCId);
      if (startRIdx === -1 || startCIdx === -1) return;
      const lines = text.split(/\r\n|\n|\r/).filter((l, i, a) => i < a.length - 1 || l !== '');
      const newRows = [...rows];
      lines.forEach((line, i) => {
        const currentRowIdx = startRIdx + i;
        while (currentRowIdx >= newRows.length) newRows.push({ id: generateId(), values: {}, expanded: true });
        const cells = line.split('\t');
        const row = { ...newRows[currentRowIdx], values: { ...newRows[currentRowIdx].values } };
        
        if (row.aggregationConfig?.enabled) {
            newRows[currentRowIdx] = row; 
            return; 
        }
        
        cells.forEach((val, j) => {
          const currentColIdx = startCIdx + j;
          if (currentColIdx >= visibleColumns.length) return;
          const col = visibleColumns[currentColIdx];
          if (row.aggregationConfig?.enabled && row.aggregationConfig.perColumn?.[col.id] && row.aggregationConfig.perColumn[col.id] !== 'none') return;

          let parsedVal: any = val;
          if (col.type === 'number' || col.type === 'currency' || col.type === 'percent') {
            const clean = val.replace(/[^0-9.-]+/g, '');
            const num = parseFloat(clean); 
            if (!isNaN(num)) parsedVal = num;
          }
          row.values[col.id] = parsedVal;
          if (row.formulas && row.formulas[col.id]) delete row.formulas[col.id];
        });
        newRows[currentRowIdx] = row;
      });
      onChange?.(newRows); setContextMenu(null); setClipboardBounds(null);
  };

  const handlePasteFromMenu = async () => {
    try {
      await navigator.clipboard.readText();
      alert("Please use Ctrl+V to paste.");
    } catch { alert("Please use Ctrl+V to paste."); }
  };

  const findNextDataBoundary = (rIdx: number, cIdx: number, rowDelta: number, colDelta: number): { r: number, c: number } => {
      const isValid = (r: number, c: number) => r >= 0 && r < visibleRows.length && c >= 0 && c < visibleColumns.length;
      const isEmpty = (r: number, c: number) => {
          const val = visibleRows[r].values[visibleColumns[c].id];
          return val === null || val === undefined || val === '';
      };

      let currR = rIdx;
      let currC = cIdx;
      
      if (rowDelta !== 0) {
          const startEmpty = isEmpty(currR, currC);
          while (isValid(currR + rowDelta, currC)) {
              const nextEmpty = isEmpty(currR + rowDelta, currC);
              if (!startEmpty && nextEmpty) break; 
              if (startEmpty && !nextEmpty) { currR += rowDelta; break; }
              currR += rowDelta;
          }
      } else if (colDelta !== 0) {
           const startEmpty = isEmpty(currR, currC);
           while (isValid(currR, currC + colDelta)) {
              const nextEmpty = isEmpty(currR, currC + colDelta);
              if (!startEmpty && nextEmpty) break;
              if (startEmpty && !nextEmpty) { currC += colDelta; break; }
              currC += colDelta;
           }
      }
      return { r: currR, c: currC };
  };

  const moveSelection = (rowDelta: number, colDelta: number, isShift = false, isCtrl = false) => {
      if (!selection.primaryRowId || !selection.primaryColumnId) return;
      const { rIdx, cIdx } = getIndices(selection.primaryRowId, selection.primaryColumnId);
      if (rIdx === -1 || cIdx === -1) return;
      
      let nextRIdx = rIdx;
      let nextCIdx = cIdx;

      if (isCtrl) {
          const next = findNextDataBoundary(rIdx, cIdx, rowDelta, colDelta);
          nextRIdx = next.r;
          nextCIdx = next.c;
      } else {
          nextRIdx += rowDelta;
          nextCIdx += colDelta;
      }

      if (nextRIdx >= visibleRows.length) { 
          if (!isShift && !isCtrl && rowDelta > 0) { handleAddRow(); return; }
          nextRIdx = visibleRows.length - 1; 
      }
      if (nextRIdx < 0) nextRIdx = 0; 
      if (nextCIdx < 0) nextCIdx = 0; 
      if (nextCIdx >= visibleColumns.length) nextCIdx = visibleColumns.length - 1;
      
      const targetRowId = visibleRows[nextRIdx].id;
      const targetColId = visibleColumns[nextCIdx].id;

      if (isShift) {
          const aRId = anchorRowId || selection.primaryRowId;
          const aCId = anchorColId || selection.primaryColumnId;
          
          if (!anchorRowId) { setAnchorRowId(aRId); setAnchorColId(aCId); }
          
          const range = getSelectionRange(visibleRows, visibleColumns, aRId, aCId, targetRowId, targetColId);
          
          const newSel: SelectionContext = {
              ...selection,
              mode: 'range',
              primaryRowId: targetRowId,
              primaryColumnId: targetColId,
              selectedRowIds: range.rowIds,
              selectedColumnIds: range.colIds
          };
          onSelectionChange?.(newSel);
      } else {
          selectCell(targetRowId, targetColId);
      }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
          if (e.key === 'z') { e.preventDefault(); if (e.shiftKey) onRedo?.(); else onUndo?.(); return; }
          if (e.key === 'c') { e.preventDefault(); handleCopy(); return; }
      }
      if (editingCell) {
          if (e.key === 'Enter') { e.preventDefault(); commitEdit(); moveSelection(1, 0); } 
          else if (e.key === 'Escape') { e.preventDefault(); setEditingCell(null); pendingCharRef.current = null; containerRef.current?.focus(); }
          return;
      }
      if (e.key === 'Escape') { if (clipboardBounds) setClipboardBounds(null); setContextMenu(null); return; }
      
      let rowDelta = 0;
      let colDelta = 0;
      
      switch (e.key) {
          case 'ArrowUp': rowDelta = -1; break;
          case 'ArrowDown': rowDelta = 1; break;
          case 'ArrowLeft': colDelta = -1; break;
          case 'ArrowRight': colDelta = 1; break;
          case 'Tab': 
              e.preventDefault(); 
              moveSelection(0, e.shiftKey ? -1 : 1, false, false);
              return;
          case 'Enter': 
              e.preventDefault(); 
              if (selection.primaryRowId && selection.primaryColumnId && !readOnly) { 
                  const row = rows.find(r => r.id === selection.primaryRowId);
                  if (row?.aggregationConfig?.enabled && row.aggregationConfig.perColumn?.[selection.primaryColumnId!] && row.aggregationConfig.perColumn[selection.primaryColumnId!] !== 'none') {
                      moveSelection(1, 0); 
                      return;
                  }
                  pendingCharRef.current = null; 
                  setEditingCell({ r: selection.primaryRowId, c: selection.primaryColumnId }); 
              } 
              break;
          case 'Delete': 
          case 'Backspace': 
              if (!editingCell && !readOnly && selection.mode !== 'none') { 
                  const newRows = rows.map(r => { 
                      if (selection.selectedRowIds.includes(r.id)) { 
                          const newVals = { ...r.values }; 
                          selection.selectedColumnIds.forEach(cId => {
                              if (r.aggregationConfig?.enabled && r.aggregationConfig.perColumn?.[cId] && r.aggregationConfig.perColumn[cId] !== 'none') return;
                              
                              // Handle different column types for clearing
                              const col = visibleColumns.find(c => c.id === cId);
                              if (col?.type === 'multi-select') {
                                  newVals[cId] = [];
                              } else if (col?.type === 'checkbox') {
                                  newVals[cId] = false;
                              } else {
                                  newVals[cId] = null;
                              }
                          });
                          return { ...r, values: newVals }; 
                      } 
                      return r; 
                  }); 
                  onChange?.(newRows); 
              } 
              break;
          case 'F2': 
              if (!editingCell && selection.primaryRowId && selection.primaryColumnId && !readOnly) { 
                  const row = rows.find(r => r.id === selection.primaryRowId);
                  if (row?.aggregationConfig?.enabled && row.aggregationConfig.perColumn?.[selection.primaryColumnId!] && row.aggregationConfig.perColumn[selection.primaryColumnId!] !== 'none') {
                      return;
                  }
                  e.preventDefault(); 
                  pendingCharRef.current = null; 
                  setEditingCell({ r: selection.primaryRowId, c: selection.primaryColumnId }); 
              } 
              break;
          default:
              if (!e.ctrlKey && !e.altKey && !e.metaKey && e.key.length === 1) {
                  if (selection.primaryRowId && selection.primaryColumnId && !readOnly) {
                       const row = rows.find(r => r.id === selection.primaryRowId);
                       if (row?.aggregationConfig?.enabled && row.aggregationConfig.perColumn?.[selection.primaryColumnId!] && row.aggregationConfig.perColumn[selection.primaryColumnId!] !== 'none') {
                           return;
                       }
                      e.preventDefault();
                      pendingCharRef.current = e.key;
                      setEditingCell({ r: selection.primaryRowId, c: selection.primaryColumnId });

                      // Story 6.5: AC-6.5.1 - Trigger formula bar when `=` is typed
                      if (e.key === '=' && onFormulaEditStart) {
                          onFormulaEditStart(selection.primaryRowId, selection.primaryColumnId, '=');
                      }
                  }
              }
              break;
      }

      if (rowDelta !== 0 || colDelta !== 0) {
          e.preventDefault();
          moveSelection(rowDelta, colDelta, e.shiftKey, e.ctrlKey || e.metaKey);
      }
  };

  const startResize = (e: React.MouseEvent, colId: string, currentWidth: number) => { e.preventDefault(); e.stopPropagation(); setResizingCol(colId); setResizeStart({ x: e.clientX, w: currentWidth }); };
  useEffect(() => {
    const move = (e: MouseEvent) => { if (!resizingCol || !resizeStart) return; const diff = e.clientX - resizeStart.x; setColWidths(prev => ({ ...prev, [resizingCol]: Math.max(50, resizeStart.w + diff) })); };
    const up = () => { setResizingCol(null); setResizeStart(null); document.body.style.cursor = ''; };
    if (resizingCol) { document.body.style.cursor = 'col-resize'; window.addEventListener('mousemove', move); window.addEventListener('mouseup', up); }
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); document.body.style.cursor = ''; };
  }, [resizingCol, resizeStart]);

  useEffect(() => { window.addEventListener('mouseup', handleMouseUp); const closeMenu = () => setContextMenu(null); window.addEventListener('click', closeMenu); return () => { window.removeEventListener('mouseup', handleMouseUp); window.removeEventListener('click', closeMenu); }; }, [selection, isDragging, isFormulaRangeCaptureActive]);
  useEffect(() => {
      if (editingCell) {
          if (editInputRef.current) {
            editInputRef.current.focus();
            if (pendingCharRef.current === null) { const len = editInputRef.current.value.length; editInputRef.current.setSelectionRange(len, len); }
          }
      }
  }, [editingCell]);

  // Story 6.5: Sync external edit value from formula bar to cell editor
  useEffect(() => {
      if (editingCell && externalEditValue !== undefined && editInputRef.current) {
          // Only update if different to avoid cursor jump
          if (editInputRef.current.value !== externalEditValue) {
              const cursorPos = editInputRef.current.selectionStart || externalEditValue.length;
              editInputRef.current.value = externalEditValue;
              // Restore cursor position
              editInputRef.current.setSelectionRange(cursorPos, cursorPos);
          }
      }
  }, [editingCell, externalEditValue]);

  const renderContent = (row: GridRow, col: GridColumn) => {
      const val = row.values[col.id];
      const cellStyle = { ...(col.style || {}), ...(row.cellStyles?.[col.id] || {}) };

      if (val === null || val === undefined) return '';

      // Story 6.4: Formula column - render with FormulaCell for visual distinction and error handling
      // AC-6.4.2: Display calculated values with locked styling
      // AC-6.4.3: Error display with tooltip
      // AC-6.4.5: Visual distinction (italic, different background)
      if (col.type === 'formula') {
          const isError = isFormulaErrorCode(val);
          return (
              <FormulaCell
                  value={val}
                  error={isError ? { code: String(val) as '#REF!' | '#VALUE!' | '#DIV/0!' | '#NAME?' | '#N/A' | '#NUM!' | '#NULL!' | '#CYCLE!' | '#ERROR!', message: 'Formula error' } : undefined}
                  semanticType={col.semanticType}
                  numberFormat={col.numberFormat}
              />
          );
      }

      // Checkbox column - render as actual checkbox
      if (col.type === 'checkbox') {
          const isChecked = val === true || val === 'true' || val === 1;
          return (
              <div className="flex items-center justify-center h-full">
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                      isChecked
                          ? 'bg-primary border-primary'
                          : 'border-muted-foreground/40 bg-transparent'
                  }`}>
                      {isChecked && (
                          <svg className="w-3 h-3 text-primary-foreground" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M2 6l3 3 5-6" />
                          </svg>
                      )}
                  </div>
              </div>
          );
      }

      if (col.type === 'rating') {
          const colorMap: Record<string, string> = {
             'Red': '#ef4444',
             'Yellow': '#eab308',
             'Green': '#22c55e',
             'Gray': '#9ca3af'
          };
          const color = colorMap[String(val)] || colorMap['Gray'];
          return (
              <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
                  <span>{val}</span>
              </div>
          );
      }
      
      if (col.type === 'multi-select') {
          const items: string[] = Array.isArray(val) ? val : (typeof val === 'string' ? val.split(',') : []);
          return (
             <div className="flex flex-wrap gap-1 items-center h-full overflow-hidden">
                {items.map((item, idx) => (
                    <span key={idx} className="grid-tag px-1.5 py-0.5 rounded text-[10px] border whitespace-nowrap">
                       {item}
                    </span>
                ))}
             </div>
          );
      }

      // Note: Formula columns are handled earlier with FormulaCell component (Story 6.4)
      // which handles semantic type formatting internally
      const effectiveType: string = col.type;

      const isNumeric = ['number', 'currency', 'percent'].includes(effectiveType);

      if (isNumeric && typeof val === 'number') {
          const decimals = cellStyle.numberFormat?.decimals ?? col.numberFormat?.decimals;
          
          if (decimals !== undefined) {
              const opts: Intl.NumberFormatOptions = { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
              if (effectiveType === 'currency') {
                  opts.style = 'currency';
                  opts.currency = 'USD';
              }
              if (effectiveType === 'percent') {
                   return `${(val * 100).toFixed(decimals)}%`;
              }
              return val.toLocaleString('en-US', opts);
          } else {
              if (effectiveType === 'currency') return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
              if (effectiveType === 'percent') return `${(val * 100).toFixed(1)}%`; 
              return val.toLocaleString('en-US');
          }
      }
      
      if ((col.type === 'date' || col.semanticType === 'date') && (typeof val === 'string' || typeof val === 'number')) {
          if (col.dateFormat) {
              const date = new Date(val);
              if (!isNaN(date.getTime())) {
                   if (col.dateFormat === 'YYYY-MM-DD') return date.toISOString().split('T')[0];
                   if (col.dateFormat === 'MM/DD/YYYY') return `${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()}`;
                   if (col.dateFormat === 'DD/MM/YYYY') return `${date.getDate()}/${date.getMonth()+1}/${date.getFullYear()}`;
              }
          }
      }

      if (typeof val === 'string' && (val.startsWith('#'))) { if (['#ERROR', '#NAME?', '#REF!', '#BLOCKED', '#SYNTAX?', '#ERR', '#NAN', '#EMPTY!', '#DIV/0!', '#VALUE!', '#N/A', '#NUM!', '#NULL!', '#CYCLE!'].includes(val)) return <span className="font-mono text-xs" title="Formula Error">{val}</span>; }
      
      return String(val);
  };

  const toggleExpand = (rowId: string) => { onChange?.(rows.map(r => r.id === rowId ? { ...r, expanded: !r.expanded } : r)); };
  const handleDragOver = (e: React.DragEvent, rowId: string) => { e.preventDefault(); e.stopPropagation(); if (dragOverRowId !== rowId) setDragOverRowId(rowId); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOverRowId(null); };
  const handleDrop = (e: React.DragEvent, rowId: string) => { e.preventDefault(); e.stopPropagation(); setDragOverRowId(null); if (e.dataTransfer.files?.length) onAttachFiles?.(rowId, e.dataTransfer.files); };

  const selectionBounds = useMemo(() => {
      if (selection.selectedRowIds.length === 0 || selection.selectedColumnIds.length === 0) return null;
      const rIndices = selection.selectedRowIds.map(id => visibleRows.findIndex(r => r.id === id)).filter(i => i !== -1);
      const cIndices = selection.selectedColumnIds.map(id => visibleColumns.findIndex(c => c.id === id)).filter(i => i !== -1);
      if (rIndices.length === 0 || cIndices.length === 0) return null;
      return { minR: Math.min(...rIndices), maxR: Math.max(...rIndices), minC: Math.min(...cIndices), maxC: Math.max(...cIndices), rowMap: new Set(selection.selectedRowIds), colMap: new Set(selection.selectedColumnIds) };
  }, [selection, visibleRows, visibleColumns]);

  const baseStickyOffset = 120; // 48px row handle + 36px attachment + 36px comment
  const stickyOffsets = useMemo(() => {
      const offsets: Record<string, number> = {};
      let currentOffset = baseStickyOffset;
      for (let i = 0; i < frozenCount && i < visibleColumns.length; i++) {
          const col = visibleColumns[i];
          offsets[col.id] = currentOffset;
          const w = colWidths[col.id] || col.width || 100;
          currentOffset += w;
      }
      return offsets;
  }, [visibleColumns, colWidths, frozenCount]);

  return (
    <div className="data-grid-container" ref={containerRef} tabIndex={0} onKeyDown={handleKeyDown} onPaste={handleNativePaste}>
      <div className="data-grid-scroll-view">
        {/* Header and body rendering remains mostly the same, just ensuring props flow */}
        <div className="data-grid-header">
          <div className="data-grid-row-handle header-handle">#</div>
          <div className="data-grid-att-col header-att-col"><IconAttachment className="w-3.5 h-3.5" /></div>
          <div className="data-grid-comm-col header-comm-col"><IconComment className="w-3.5 h-3.5" /></div>
          {visibleColumns.map((col, idx) => {
              const w = colWidths[col.id] || col.width || 100;
              const isColSelected = selection.mode === 'column' && selection.selectedColumnIds.includes(col.id);
              const isFrozen = idx < frozenCount;
              const stickyStyle: React.CSSProperties = isFrozen ? { position: 'sticky', left: stickyOffsets[col.id], zIndex: 50 } : {};
              const isSorted = sortConfig?.colId === col.id;
              const isDragTarget = reorderTargetColId === col.id;

              return (
                  <div 
                      key={col.id} 
                      className={`data-grid-header-cell justify-between group ${isColSelected ? 'col-selected' : ''} ${isFrozen ? 'sticky-data-col' : ''} ${isDragTarget ? 'col-drag-target' : ''}`} 
                      style={{ width: w, minWidth: w, ...stickyStyle }} 
                      draggable
                      onDragStart={(e) => handleColDragStart(e, col.id)}
                      onDragOver={(e) => handleColDragOver(e, col.id)}
                      onDragLeave={handleColDragLeave}
                      onDrop={(e) => handleColDrop(e, col.id)}
                      onClick={(e) => handleColumnHeaderClick(col.id, e)}
                      onContextMenu={(e) => handleHeaderContextMenu(e, col.id)}
                  >
                      <div className="flex items-center gap-1 truncate flex-1 pointer-events-none">
                          {col.type === 'formula' && <IconLock className="w-3 h-3 grid-icon-muted" />}
                          {col.label}
                          {isSorted && (
                              <span className="grid-icon-accent ml-1">
                                  {sortConfig?.direction === 'asc' ? <IconSortAsc className="w-3 h-3" /> : <IconSortDesc className="w-3 h-3" />}
                              </span>
                          )}
                      </div>
                      <div
                        className="grid-header-menu-btn p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity mr-1 cursor-pointer"
                        onClick={(e) => handleMenuButtonClick(e, col.id)}
                      >
                         <IconMoreVertical className="w-4 h-4" />
                      </div>
                      <div className="data-grid-resize-handle" onMouseDown={(e) => startResize(e, col.id, w)} />
                  </div>
              );
          })}
        </div>

        {visibleRows.length === 0 && emptyState ? (
            <div className="grid-empty-state">
                <div className="grid-empty-state-content">
                    {emptyState}
                </div>
            </div>
        ) : (
            <>
            {visibleRows.map((row, rIdx) => {
                const isRowSelected = selection.mode === 'row' && selection.selectedRowIds.includes(row.id);
                const depth = depthMap.get(row.id) || 0;
                const hasChildren = hasChildrenMap.get(row.id);
                const isDragTarget = dragOverRowId === row.id || reorderTargetId === row.id;

                let rowTypeClass = '';
                const isSpecialRow = ['group', 'subtotal', 'total'].includes(row.rowType || '');
                if (row.rowType === 'group') rowTypeClass = 'row-group';
                else if (row.rowType === 'subtotal') rowTypeClass = 'row-subtotal';
                else if (row.rowType === 'total') rowTypeClass = 'row-total';

                return (
                    <div 
                        key={row.id} 
                        className={`data-grid-row ${rowTypeClass} ${isRowSelected ? 'selected' : ''} ${isDragTarget ? 'drag-target' : ''} animate-slide-down`} 
                        onDragOver={e => {
                             if (reorderDragRowId) {
                                 handleRowDragOver(e, row.id);
                             } else {
                                 handleDragOver(e, row.id);
                             }
                        }} 
                        onDragLeave={(e) => {
                             if (reorderDragRowId) handleRowDragLeave();
                             else handleDragLeave(e);
                        }} 
                        onDrop={e => {
                             if (reorderDragRowId) {
                                 handleRowDrop(e, row.id);
                             } else {
                                 handleDrop(e, row.id);
                             }
                        }}
                    >
                        <div
                            className={`data-grid-row-handle ${isRowSelected ? 'selected' : ''}`}
                            style={{ cursor: isSpecialRow ? 'grab' : 'pointer' }}
                            onClick={(e) => handleRowHeaderClick(row.id, e)}
                            onContextMenu={(e) => handleContextMenu(e, row.id)}
                            draggable={isSpecialRow}
                            onDragStart={(e) => isSpecialRow && handleRowDragStart(e, row.id)}
                        >
                            <div className="row-handle-inner">
                                {isSpecialRow ? <IconSummary className="w-3.5 h-3.5 grid-icon-accent" /> : (rIdx + 1)}
                            </div>
                        </div>
                        <div className={`data-grid-att-col ${isRowSelected ? 'selected' : ''}`}>
                            <div className={`icon-btn ${(row.attachmentCount || 0) > 0 ? 'has-items att-active' : 'att-inactive'}`} onClick={(e) => { e.stopPropagation(); onOpenAttachments?.(row.id); }} title={`${row.attachmentCount || 0} Attachments`}><IconAttachment className="w-3.5 h-3.5" /></div>
                        </div>
                        <div className={`data-grid-comm-col ${isRowSelected ? 'selected' : ''}`}>
                            <div className={`icon-btn ${(row.commentCount || 0) > 0 ? 'has-items comm-active' : 'comm-inactive'}`} onClick={(e) => { e.stopPropagation(); onOpenComments?.(row.id); }} title={`${row.commentCount || 0} Comments`}><IconComment className="w-3.5 h-3.5" /></div>
                        </div>
                        {visibleColumns.map((col, cIdx) => {
                            const w = colWidths[col.id] || col.width || 100;
                            const isSelected = selectionBounds?.rowMap.has(row.id) && selectionBounds?.colMap.has(col.id);
                            const isPrimary = selection.primaryRowId === row.id && selection.primaryColumnId === col.id;
                            const isEditing = editingCell?.r === row.id && editingCell?.c === col.id;
                            const validationMsg = validationErrors ? validationErrors[`${row.id}:${col.id}`] : null;
                            const validation = validateCell(row.values[col.id], col);
                            const isInvalid = !!validationMsg || (validation && !!validation.message);
                            
                            const cellStyle = { ...(col.style || {}), ...(row.cellStyles?.[col.id] || {}) };
                            
                            const isAggregated = row.aggregationConfig?.enabled && 
                                                 row.aggregationConfig.perColumn?.[col.id] && 
                                                 row.aggregationConfig.perColumn[col.id] !== 'none';

                            let borderClass = '';
                            if (isSelected && selectionBounds) {
                                if (rIdx === selectionBounds.minR) borderClass += ' cell-sel-top';
                                if (rIdx === selectionBounds.maxR) borderClass += ' cell-sel-bottom';
                                if (cIdx === selectionBounds.minC) borderClass += ' cell-sel-left';
                                if (cIdx === selectionBounds.maxC) borderClass += ' cell-sel-right';
                                if (rIdx === selectionBounds.minR && cIdx === selectionBounds.minC) borderClass += ' cell-sel-tl';
                                if (rIdx === selectionBounds.minR && cIdx === selectionBounds.maxC) borderClass += ' cell-sel-tr';
                                if (rIdx === selectionBounds.maxR && cIdx === selectionBounds.minC) borderClass += ' cell-sel-bl';
                                if (rIdx === selectionBounds.maxR && cIdx === selectionBounds.maxC) borderClass += ' cell-sel-br';
                            }
                            
                            const isCopied = clipboardBounds && rIdx >= clipboardBounds.minR && rIdx <= clipboardBounds.maxR && cIdx >= clipboardBounds.minC && cIdx <= clipboardBounds.maxC;
                            if (isCopied && clipboardBounds) {
                                if (rIdx === clipboardBounds.minR) borderClass += ' clip-top';
                                if (rIdx === clipboardBounds.maxR) borderClass += ' clip-bottom';
                                if (cIdx === clipboardBounds.minC) borderClass += ' clip-left';
                                if (cIdx === clipboardBounds.maxC) borderClass += ' clip-right';
                            }

                            const fontSizeMap = { sm: '0.75rem', md: '0.875rem', lg: '1rem' };
                            const isFrozen = cIdx < frozenCount;
                            
                            const zIndex = isFrozen ? (isPrimary ? 31 : 30) : undefined;

                            const stickyStyle: React.CSSProperties = isFrozen ? { 
                                position: 'sticky', 
                                left: stickyOffsets[col.id], 
                                zIndex, 
                                backgroundColor: isRowSelected ? undefined : (cellStyle?.backgroundColor || undefined)
                            } : {};

                            const inlineStyle: React.CSSProperties = {
                                width: w, minWidth: w,
                                backgroundColor: isRowSelected ? undefined : cellStyle?.backgroundColor,
                                color: cellStyle?.textColor,
                                fontWeight: (cellStyle?.bold || row.rowType === 'group' || row.rowType === 'total' || row.rowType === 'subtotal') ? 'bold' : 'normal',
                                fontStyle: cellStyle?.italic ? 'italic' : 'normal',
                                textDecoration: cellStyle?.underline ? 'underline' : 'none',
                                fontSize: cellStyle?.fontSize ? fontSizeMap[cellStyle.fontSize] : undefined,
                                textAlign: cellStyle?.align || 'left',
                                whiteSpace: cellStyle?.wrapText ? 'normal' : 'nowrap',
                                outline: isInvalid ? '1px solid #ef4444' : undefined,
                                outlineOffset: isInvalid ? '-1px' : undefined,
                                ...stickyStyle
                            };

                            const effectiveIsEditing = isEditing && !isAggregated;
                            
                            let editValue = '';
                            if (effectiveIsEditing) {
                                if (pendingCharRef.current !== null) { editValue = pendingCharRef.current; } else { 
                                    const raw = row.values[col.id];
                                    if (col.type === 'multi-select' && Array.isArray(raw)) {
                                        editValue = raw.join(', ');
                                    } else {
                                        editValue = row.formulas?.[col.id] ? row.formulas[col.id].raw : (raw === null || raw === undefined ? '' : String(raw));
                                    }
                                }
                            }

                            return (
                                <div 
                                    key={col.id} 
                                    id={`cell-${row.id}-${col.id}`}
                                    className={`data-grid-cell ${isSelected ? 'range-selected' : ''} ${isPrimary ? 'active' : ''} ${borderClass} ${isFrozen ? 'sticky-data-col shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : ''}`} 
                                    style={inlineStyle} 
                                    onMouseDown={(e) => handleCellMouseDown(row.id, col.id, e)} 
                                    onMouseEnter={() => handleCellMouseEnter(row.id, col.id)} 
                                    onClick={(e) => handleCellClick(row.id, col.id, e)} 
                                    onContextMenu={(e) => handleContextMenu(e, row.id, col.id)} 
                                    onDoubleClick={() => handleDoubleClick(row.id, col.id)} 
                                    title={validationMsg || validation?.message}
                                >
                                    {col.isPrimary && (
                                        <div style={{ paddingLeft: depth * 16 }} className="flex items-center h-full mr-1">
                                            {hasChildren ? (
                                                <span className="hierarchy-caret" onClick={(e) => { e.stopPropagation(); toggleExpand(row.id); }}>{row.expanded !== false ? <IconChevronDown className="w-3 h-3" /> : <IconChevronRight className="w-3 h-3" />}</span>
                                            ) : <span className="hierarchy-spacer w-4 h-4 inline-block mr-1" />}
                                        </div>
                                    )}
                                    
                                    {isAggregated && !col.isPrimary && (
                                        <span className="text-[10px] grid-icon-accent mr-1" title="Aggregated value">∑</span>
                                    )}

                                    {effectiveIsEditing ? (
                                        (col.type === 'select' || col.type === 'rating') ? (
                                            <>
                                                <input 
                                                    ref={editInputRef}
                                                    list={`list-${row.id}-${col.id}`}
                                                    defaultValue={editValue}
                                                    onBlur={() => commitEdit()}
                                                    onKeyDown={e => { 
                                                        if(e.key === 'Enter') commitEdit(); 
                                                        if(e.key==='Escape') { 
                                                            setEditingCell(null); 
                                                            pendingCharRef.current = null; 
                                                            containerRef.current?.focus(); 
                                                        } 
                                                    }}
                                                    className="grid-cell-input w-full h-full px-1 outline-none border-none"
                                                    autoFocus
                                                    autoComplete="off"
                                                />
                                                <datalist id={`list-${row.id}-${col.id}`}>
                                                    {(col.type === 'rating' ? ['Red', 'Yellow', 'Green', 'Gray'] : (col.options || [])).map(opt => <option key={opt} value={opt} />)}
                                                </datalist>
                                            </>
                                        ) : col.type === 'multi-select' && col.options ? (
                                            <div className="relative w-full h-full">
                                               <input 
                                                    ref={editInputRef}
                                                    defaultValue={editValue}
                                                    onBlur={() => commitEdit()}
                                                    onKeyDown={e => { if(e.key === 'Enter') commitEdit(); if(e.key==='Escape') { setEditingCell(null); pendingCharRef.current = null; containerRef.current?.focus(); } }}
                                                    className="grid-cell-input w-full h-full px-1 outline-none"
                                                    autoFocus
                                                    placeholder="tag1, tag2..."
                                                />
                                                <div className="grid-tag-popup absolute bottom-full left-0 mb-1 border shadow-lg rounded p-2 z-50 min-w-[150px] hidden group-focus-within:block">
                                                    <div className="grid-tag-popup-label text-xs mb-1 font-bold">Available Tags:</div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {col.options.map(opt => (
                                                            <span key={opt} className="grid-tag-option px-1.5 py-0.5 rounded text-xs border cursor-pointer" onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                if (editInputRef.current) {
                                                                    const val = editInputRef.current.value;
                                                                    const parts = val.split(',').map(s=>s.trim()).filter(s=>s);
                                                                    if(!parts.includes(opt)) {
                                                                        parts.push(opt);
                                                                        editInputRef.current.value = parts.join(', ');
                                                                    }
                                                                }
                                                            }}>
                                                                {opt}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <input
                                                ref={editInputRef}
                                                defaultValue={editValue}
                                                onBlur={() => commitEdit()}
                                                onChange={(e) => onCellEditChange?.(e.target.value)}
                                                onKeyDown={e => {
                                                    if(e.key === 'Enter') commitEdit();
                                                    if(e.key==='Escape') {
                                                        setEditingCell(null);
                                                        pendingCharRef.current = null;
                                                        containerRef.current?.focus();
                                                        onCellEditChange?.(''); // Clear formula bar on escape
                                                    }
                                                }}
                                                className="grid-cell-input w-full h-full px-1"
                                            />
                                        )
                                    ) : (
                                        (col.type === 'select' || col.semanticType === 'status') ? (
                                            <span className={`grid-status-badge px-2 py-0.5 rounded-full text-xs font-medium truncate ${row.values[col.id] === 'Done' || row.values[col.id] === 'Complete' ? 'status-done' : row.values[col.id] === 'In Progress' ? 'status-progress' : row.values[col.id] === 'Pending' || row.values[col.id] === 'Not Started' ? 'status-pending' : 'status-default'}`}>{renderContent(row, col)}</span>
                                        ) : <span className="w-full pointer-events-none" style={{ whiteSpace: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis' }}>{renderContent(row, col)}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
            <div
                className={`grid-drop-zone h-16 w-full transition-colors flex items-center justify-center border-t border-dashed border-transparent ${reorderDragRowId ? 'can-drop' : ''}`}
                onDragOver={(e) => {
                    if(reorderDragRowId) {
                        e.preventDefault();
                        e.stopPropagation();
                        setReorderTargetId('__END__');
                    }
                }}
                onDragLeave={() => setReorderTargetId(null)}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if(reorderDragRowId) {
                         onRowReorder?.(reorderDragRowId, '__END__');
                    }
                    setReorderDragRowId(null);
                    setReorderTargetId(null);
                }}
            >
               {reorderDragRowId && reorderTargetId === '__END__' && <span className="grid-drop-text text-sm font-medium">Move to bottom</span>}
            </div>
            </>
        )}
      </div>
      {contextMenu && createPortal(
        <div
          ref={contextMenuRef}
          className="context-menu"
          style={{
            // Position menu at click location, clamped to viewport
            position: 'fixed',
            top: Math.min(contextMenu.y, window.innerHeight - 320),
            left: Math.min(contextMenu.x, window.innerWidth - 180),
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
        >
            {contextMenu.type === 'header' ? (
                <>
                    <div className="context-menu-item" onClick={handleEditColumn}>
                        <span>Edit Column</span>
                        <IconSettings className="w-4 h-4 context-menu-icon" />
                    </div>
                    <div className="context-menu-item" onClick={handleInspectColumnTypes}>
                        <span>Inspect Column Types</span>
                        <IconInfo className="w-4 h-4 context-menu-icon" />
                    </div>
                    <div className="context-menu-item" onClick={handleHideColumn}>
                        <span>Hide Column</span>
                        <IconEyeOff className="w-4 h-4 context-menu-icon" />
                    </div>
                     {/* Freeze functionality disabled for now - will be added later */}
                     <div className="context-menu-divider"></div>
                     <div className="context-menu-item" onClick={handleSortTrigger}>
                        <span>Sort {sortConfig?.direction === 'asc' ? 'Desc' : 'Asc'}</span>
                     </div>
                     <div className="context-menu-divider"></div>
                     <div className="context-menu-item" onClick={() => { onInsertColumn?.(contextMenu.targetId, 'left'); setContextMenu(null); }}>Insert column left</div>
                     <div className="context-menu-item" onClick={() => { onInsertColumn?.(contextMenu.targetId, 'right'); setContextMenu(null); }}>Insert column right</div>
                     <div className="context-menu-item context-menu-danger" onClick={(e) => {
                        e.stopPropagation();
                        onDeleteColumn?.(contextMenu.targetId);
                        setContextMenu(null);
                     }}>Delete column</div>
                </>
            ) : (
                <>
                    <div className="context-menu-item" onClick={handleCopy}><span>Copy</span><span className="context-menu-shortcut">Ctrl+C</span></div>
                    <div className="context-menu-item" onClick={handlePasteFromMenu}><span>Paste</span><span className="context-menu-shortcut">Ctrl+V</span></div>
                    <div className="context-menu-divider"></div>
                    <div className="context-menu-item" onClick={() => handleInsertRow('above')}>Insert row above</div>
                    <div className="context-menu-item" onClick={() => handleInsertRow('below')}>Insert row below</div>
                    <div className="context-menu-item" onClick={handleDuplicateRow}>Duplicate row</div>
                    <div className="context-menu-divider"></div>
                    <div className="context-menu-item" onClick={() => { if (onRowConfig && contextMenu) { onRowConfig(contextMenu.targetId); setContextMenu(null); } }}>
                        <span>Row Configuration</span>
                        <IconSettings className="w-4 h-4 context-menu-icon" />
                    </div>
                    <div className="context-menu-divider"></div>
                    <div className="context-menu-item context-menu-danger" onClick={handleDeleteRows}>Delete row(s)</div>
                </>
            )}
        </div>,
        document.body
      )}
    </div>
  );
};

// Default export for standard view component pattern
export default DataGrid;